import { normalizeTimetable } from './utils/timetable.js'
import { normalizeBotSettings } from './utils/botSettings.js'
import { normalizeCachedDashboardStatus } from './utils/runtimeStatus.js'

const ActionType = Object.freeze({
    GetUUID: 0,
    GetUsers: 1,
    SetUser: 2,
    RemoveUser: 3,
    AddUser: 4,
    GetLogs: 5,
    Reset: 6,
    StatusUser: 7,
    Unknown: 8,
    GetChannels : 18,
    GetAllianceChat: 20,
    SendAllianceChat: 21,
    RefreshAttackTemplateInventory: 22,
    SetAccountRole: 23,
    GetModerationUsers: 24,
    ApproveModerationUser: 25,
    ResumeBot: 26,
    RefreshAttackTemplatePresets: 27,
    RequestReconnect: 28,
    LuckyWheelSpin: 29,
    ManualCastleDefenseRecall: 31,
    ManualCastleDefenseSend: 32,
    PurchasePrivateOffer: 34
})
const GetActionTypeName = id => 
    Object.keys(ActionType).find(key => ActionType[key] === id) ?? 
        ActionType.Unknown

const ErrorType = Object.freeze({
    Success: 0,
    UnknownAction: 1,
    Unauthenticated: 2,
    Authentication: 3,
    Generic: 4,
    UnknownError: 5,
})

const GetErrorTypeName = id => 
    Object.keys(ErrorType).find(key => ErrorType[key] === id) ??
        "UnknownError"

const LogLevel = Object.freeze({
    Info: 0,
    Warn: 1,
    Error: 2
})

const normalizeExternalEventType = (value, fallbackExternalEvent = false) => {
    const normalizedValue = String(value ?? '').trim().toLowerCase()

    if (!normalizedValue)
        return fallbackExternalEvent ? 'auto' : ''

    if (['auto', 'legacy', 'or_bth', 'or/bth'].includes(normalizedValue))
        return 'auto'

    if (['outer_realms', 'outer realms', 'outerrealms', 'or'].includes(normalizedValue))
        return 'outer_realms'

    if (['battle_for_the_throne', 'battle for the throne', 'battleforthethrone', 'bth'].includes(normalizedValue))
        return 'battle_for_the_throne'

    return fallbackExternalEvent ? 'auto' : ''
}

const parseCachedDashboardStatus = value => {
    if (value && typeof value === 'object' && !Array.isArray(value))
        return normalizeCachedDashboardStatus(value)

    if (typeof value !== 'string' || !value.trim())
        return undefined

    try {
        return normalizeCachedDashboardStatus(JSON.parse(value))
    } catch {
        return undefined
    }
}

class User {
    constructor(obj) {
      this.id = Number(obj.id) 
      this.state = Number(obj.state)
      this.name = String(obj.name) 
      this.pass = String(obj.pass ? obj.pass : "")
      this.plugins = obj.plugins
      this.lt = Number(obj.lt)
      this.externalEventType = normalizeExternalEventType(obj?.externalEventType, obj?.externalEvent)
      this.externalEvent = Boolean(this.externalEventType)
      this.server = Number(obj?.server)
      this.platform = obj?.platform === 'e4k' ? 'e4k' : 'gge'
      this.uuid = obj?.uuid ? String(obj.uuid) : undefined
      this.ownerUsername = obj?.ownerUsername ? String(obj.ownerUsername) : undefined
      this.ownerPrivilege = Number(obj?.ownerPrivilege ?? 0)
      this.ownerRole = String(obj?.ownerRole ?? '')
      this.canViewPassword = obj?.canViewPassword === undefined ? true : Boolean(obj.canViewPassword)
      this.passwordHidden = Boolean(obj?.passwordHidden)
      this.approved = Boolean(obj?.approved)
      this.approvedUntil = [undefined, null, ""].includes(obj?.approvedUntil) ? undefined : Number(obj.approvedUntil)
      this.callSetupApproved = Boolean(obj?.callSetupApproved)
      this.allianceCallApproved = Boolean(obj?.allianceCallApproved)
      this.allianceRiftPortalApproved = Boolean(obj?.allianceRiftPortalApproved)
      this.grandTournamentApproved = Boolean(obj?.grandTournamentApproved)
      this.cachedLeagueDivisionRank = obj?.cachedLeagueDivisionRank === undefined ? undefined : Number(obj.cachedLeagueDivisionRank)
      this.cachedLeagueSeasonRank = obj?.cachedLeagueSeasonRank === undefined ? undefined : Number(obj.cachedLeagueSeasonRank)
      this.cachedLeagueDivisionId = obj?.cachedLeagueDivisionId === undefined ? undefined : Number(obj.cachedLeagueDivisionId)
      this.cachedLeagueDivisionSize = obj?.cachedLeagueDivisionSize === undefined ? undefined : Number(obj.cachedLeagueDivisionSize)
      this.cachedResourceAlerts = Array.isArray(obj?.cachedResourceAlerts) ? obj.cachedResourceAlerts : []
      this.cachedDashboardStatus = parseCachedDashboardStatus(obj?.cachedDashboardStatus)
      this.botSettings = normalizeBotSettings(obj?.botSettings)
      this.timetable = normalizeTimetable(obj?.timetable)
    }
  }
export { ActionType, GetActionTypeName, ErrorType, GetErrorTypeName, LogLevel, User }
